import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';

import { Button } from '@ui/components/ui/button';

import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { clientAccessPath, productTabPath } from '@app/constants/url';
import { cardClass } from '@app/constants/styles';
import { capitalize } from '@app/utils';
import { ClientAccessRole, ProductEntity } from '@data/utils/types';

// The two groups the client's own access list draws, in its order, so both screens read the same way.
const GROUPS: { role?: ClientAccessRole; titleKey: string }[] = [
  { role: ClientAccessRole.admin, titleKey: 'access.admins' },
  { role: ClientAccessRole.read, titleKey: 'access.viewOnly' },
  // A reader on a product that belongs to no client company has no standing to group them by.
  { role: undefined, titleKey: 'productAccess.otherReaders' },
];

const displayName = (person: { firstName?: string; lastName?: string; email: string }): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || person.email;

/**
 * Who at the client can read this product.
 *
 * Read-only on purpose: access is granted per person, so it is changed where the people are — the client's
 * own page, which this card's one button opens at that list.
 */
const ClientAccessCard = ({
  product,
  organizationId,
}: {
  product: ProductEntity;
  organizationId: string;
}): ReactElement => {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const location = useLocation();

  const people = product.clientAccess ?? [];
  // Counted against the client's own people, so only the readers who hold a standing there count: a
  // reader from before client companies belongs to no client, and "3 of 2" would be the result.
  const atThisClient = people.filter(person => person.role).length;
  const clientName = product.clientCompanyName;

  // Back returns here rather than to the clients list, because that is where the reader came from.
  const manage = (): void =>
    transitionNavigate(clientAccessPath(organizationId, product.clientCompanyId ?? ''), 'forward', {
      state: {
        backPath: `${productTabPath(organizationId, product.id, 'settings')}${location.search}`,
        backLabel: t('actions.backToName', { name: capitalize(product.name) ?? product.name }),
      },
    });

  return (
    <section className={`${cardClass} mb-6`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-foreground">{t('productAccess.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('productAccess.hint')}</p>
        </div>
        {product.clientCompanyId ? (
          <Button variant="outline" size="sm" onClick={manage}>
            {t('productAccess.manage')}
            <ArrowRight className="size-4" />
          </Button>
        ) : null}
      </div>

      {people.length ? (
        <div className="space-y-4">
          {GROUPS.map(group => {
            const inGroup = people.filter(person => person.role === group.role);
            if (!inGroup.length) return null;
            return (
              <section key={group.titleKey}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t(group.titleKey)}
                </p>
                {inGroup.map(person => {
                  const name = displayName(person);
                  return (
                    <div
                      key={person.membershipId}
                      className="flex items-center gap-3 border-b border-border py-2.5 last:border-b-0"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <Mail className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{name}</p>
                        {name !== person.email && (
                          <p className="truncate text-xs text-muted-foreground">{person.email}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {clientName ? t('productAccess.nobody', { name: clientName }) : t('productAccess.noClient')}
        </p>
      )}

      {/* Only worth saying once somebody has access: how much of the client is still outside it. */}
      {atThisClient && clientName && product.clientPeopleCount ? (
        <p className="mt-4 text-xs text-muted-foreground">
          {t('productAccess.ofPeople', { shown: atThisClient, total: product.clientPeopleCount, name: clientName })}
        </p>
      ) : null}
    </section>
  );
};

export default ClientAccessCard;
